import React, { useEffect, useState } from 'react';
import Icon from './IconMapper';
import tommysLogoWhite from '../../../assets/images/tommys/tommys-logo-white.png';
import heroImg from '../../../assets/images/tommys/hero.jpg';
import catBait from '../../../assets/images/tommys/cat-bait.png';
import catShop from '../../../assets/images/tommys/cat-shop.png';
import catRigs from '../../../assets/images/tommys/cat-rigs.png';
import catTerminal from '../../../assets/images/tommys/cat-terminal.png';
import '../../../assets/css/tommys_tackle_strip.css';

const AWIN = {
  primary: 'https://tidd.ly/4tQ6nqG',
  bait: 'https://tidd.ly/4wEMbuv',
  shop: 'https://tidd.ly/4unZCxq',
  rigs: 'https://tidd.ly/4dvsfRT',
  terminal: 'https://tidd.ly/4fx9Ab0',
  sbplus: `${process.env.REACT_APP_SBPLUS_URL}/sign-up`,
};

const CATEGORIES = [
  { id: 'bait', name: 'Bait', img: catBait, href: AWIN.bait },
  { id: 'shop', name: 'Shop Korda', img: catShop, href: AWIN.shop },
  { id: 'rigs', name: 'Carp Products', img: catRigs, href: AWIN.rigs },
  {
    id: 'terminal',
    name: 'Terminal Tackle',
    img: catTerminal,
    href: AWIN.terminal,
  },
];

/* Click-to-reveal discount code with per-character decode animation:
   each slot scrambles random chars for ~250ms then locks to the real
   character. CSS keyframe `tt-code-glitch` (in tommys_tackle_strip.css)
   handles the per-char blur/skew/drop-in driven by the --i index.
   Respects prefers-reduced-motion. */
function CodeReveal({ code }) {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [displayed, setDisplayed] = useState('');

  useEffect(() => {
    if (!revealed) {
      setDisplayed('');
      return undefined;
    }
    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      setDisplayed(code);
      return undefined;
    }

    const charset = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    const perChar = 110;
    const scrambleDur = 250;
    const tickInterval = 45;
    const timers = [];

    let current = code
      .split('')
      .map(() => charset[Math.floor(Math.random() * charset.length)]);
    setDisplayed(current.join(''));

    for (let i = 0; i < code.length; i++) {
      const lockAt = i * perChar + scrambleDur;
      const tick = setInterval(() => {
        current = current.slice();
        current[i] = charset[Math.floor(Math.random() * charset.length)];
        setDisplayed(current.join(''));
      }, tickInterval);
      timers.push(tick);
      const lock = setTimeout(() => {
        clearInterval(tick);
        current = current.slice();
        current[i] = code[i];
        setDisplayed(current.join(''));
      }, lockAt);
      timers.push(lock);
    }

    return () => {
      timers.forEach((t) => {
        clearInterval(t);
        clearTimeout(t);
      });
    };
  }, [revealed, code]);

  useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => setCopied(false), 1800);
    return () => clearTimeout(t);
  }, [copied]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
    } catch (e) {
      const t = document.createElement('textarea');
      t.value = code;
      document.body.appendChild(t);
      t.select();
      try {
        document.execCommand('copy');
      } catch (_) {}
      document.body.removeChild(t);
    }
    setCopied(true);
  };

  const lockedProps = revealed
    ? {}
    : {
        role: 'button',
        tabIndex: 0,
        onClick: () => setRevealed(true),
        onKeyDown: (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setRevealed(true);
          }
        },
        'aria-label': 'Tap to reveal your discount code',
      };

  return (
    <div
      className={`tt-code ${revealed ? 'revealed' : 'locked'}`}
      {...lockedProps}
    >
      <div className='label'>
        <small>
          {revealed ? 'Your discount code' : 'Use this code at checkout'}
        </small>
        {revealed ? (
          <span className='value' aria-label={`Discount code ${code}`}>
            {displayed.split('').map((ch, i) => (
              <span key={i} className='ch' style={{ '--i': i }}>
                {ch}
              </span>
            ))}
          </span>
        ) : (
          <span className='scratch'>
            <Icon name='lock' size={13} /> Tap to reveal
          </span>
        )}
      </div>
      {revealed ? (
        <button
          type='button'
          className={`tt-code-copy ${copied ? 'copied' : ''}`}
          onClick={handleCopy}
          aria-label='Copy discount code'
        >
          <Icon name={copied ? 'check' : 'copy'} size={14} />
          {copied ? 'Copied' : 'Copy'}
        </button>
      ) : (
        <span className='tt-code-reveal' aria-hidden='true'>
          <Icon name='eye' size={14} /> Reveal code
        </span>
      )}
    </div>
  );
}

const TommysTackleStrip = React.forwardRef(function TommysTackleStrip(
  { code },
  ref
) {
  return (
    <section className='tt-strip' ref={ref} aria-labelledby='tt-headline'>
      <div className='tt-strip-top'>
        <div className='tt-strip-copy'>
          <span className='tt-strip-eyebrow'>
            <Icon name='gift' size={12} /> You&apos;ve unlocked an exclusive
            perk
          </span>
          <h2 id='tt-headline' className='tt-strip-headline'>
            A little something for your <em>next session</em>, on us.
          </h2>
          <p className='tt-strip-sub'>
            We&apos;ve teamed up with Tommy&apos;s Tackle so you can save on
            bait, rigs and the bits you always forget — delivered free, ahead of
            your session.
          </p>

          <div className='tt-strip-chips'>
            <span className='tt-strip-chip headline'>
              <Icon name='badge-percent' size={14} /> £6 off your first order
            </span>
          </div>

          <div className='tt-strip-actions'>
            <a
              className='tt-cta-primary'
              href={AWIN.primary}
              target='_blank'
              rel='noopener sponsored noreferrer'
            >
              Shop Tommy&apos;s Tackle <Icon name='arrow-up-right' size={16} />
            </a>
          </div>

          <CodeReveal code={code} />

          <p className='tt-strip-disclaimer'>
            Offer applies on orders £28+. Free shipping on orders over £40. Single use per
            customer. Code expires at the end of the current month. Delivery prior to
            session not guaranteed.
          </p>
        </div>

        <div
          className='tt-strip-hero'
          style={{ backgroundImage: `url(${heroImg})` }}
        >
          <div className='partner-plate' aria-label="Tommy's Tackle">
            <span className='partner-eyebrow'>Our partner shop</span>
            <img
              className='partner-logo'
              src={tommysLogoWhite}
              alt="Tommy's Tackle"
            />
          </div>
        </div>
      </div>

      <div className='tt-cats-row'>
        {CATEGORIES.map((c) => (
          <a
            key={c.id}
            className='tt-cat'
            href={c.href}
            target='_blank'
            rel='noopener sponsored noreferrer'
            aria-label={`Shop ${c.name} at Tommy's Tackle`}
            style={{ backgroundImage: `url(${c.img})` }}
          >
            <span className='name'>{c.name}</span>
          </a>
        ))}
      </div>

      <div className='tt-strip-foot'>
        <Icon name='sparkles' size={14} />
        <span>
          <strong>SB+ members</strong> get 10% off every order, forever.{' '}
          <a href={AWIN.sbplus} target='_blank' rel='noopener noreferrer'>
            Learn more
          </a>
        </span>
      </div>
    </section>
  );
});

export default TommysTackleStrip;
